import { sql } from "drizzle-orm";
import {
  pgTable,
  uuid,
  text,
  integer,
  boolean,
  numeric,
  date,
  timestamp,
  jsonb,
  unique,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

// Cross-domain imports (relative — NEVER use @shared/ alias, it breaks esbuild)
import { organisations } from "./schema-security.js";

// ============================================================================
// STAFF-ADOPTION FALSIFIER — read-side snapshot store (EDG-7586 / spec EDG-7573)
//
// Persists weekly Adoption Falsifier Index (AFI) readings so the 3-week
// false-confidence alarm window (§6) and trend are auditable. This is a
// read-side table only — populated by the weekly job from data staff already
// generate by using the tool. It is NOT on any user write path and adds zero
// staff data-entry burden (preserves constraint C1). See spec §7.
// ============================================================================

export const staffAdoptionFalsifierSnapshots = pgTable(
  "crm_x_staff_adoption_falsifier_snapshots",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    organisationId: uuid("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    // Monday (AEST) of the ISO week this snapshot covers.
    weekStart: date("week_start").notNull(),
    // §5 lagging metric.
    qualifyingCount: integer("qualifying_count").notNull().default(0),
    sidewaysHeld: boolean("sideways_held").notNull().default(false),
    verdict: text("verdict").notNull(), // 'BET-SUPPORTED' | 'NOT-YET'
    // §5 leading metric (drives the alarm).
    momentum: integer("momentum").notNull().default(0),
    // §6 cold-start suppression marker.
    noLiveDeals: boolean("no_live_deals").notNull().default(false),
    reason: text("reason"), // e.g. 'no-live-deals'
    // L2 — fraction of live deals with >=1 human transition (low => lifecycle non-adoption).
    humanTransitionCoverage: numeric("human_transition_coverage", { precision: 5, scale: 4 })
      .notNull()
      .default("0"),
    // Drill-down (C1 / DoD #2): [{ dealId, primaryCarrierId, sidewaysHeld }].
    qualifyingDeals: jsonb("qualifying_deals").notNull().default(sql`'[]'::jsonb`),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    // One canonical snapshot per org per week (idempotent weekly job).
    orgWeekUnique: unique("crm_x_saf_snapshots_org_week_unique").on(
      table.organisationId,
      table.weekStart,
    ),
  }),
);

const qualifyingDealSchema = z.object({
  dealId: z.string(),
  primaryCarrierId: z.string(),
  sidewaysHeld: z.boolean(),
});

export const insertStaffAdoptionFalsifierSnapshotSchema = createInsertSchema(
  staffAdoptionFalsifierSnapshots,
  {
    organisationId: z.string().uuid(),
    weekStart: z.string(),
    verdict: z.enum(["BET-SUPPORTED", "NOT-YET"]),
    reason: z.string().nullable().optional(),
    humanTransitionCoverage: z.string().optional(),
    qualifyingDeals: z.array(qualifyingDealSchema).default([]),
  },
).omit({ id: true, createdAt: true });

export const selectStaffAdoptionFalsifierSnapshotSchema = createSelectSchema(
  staffAdoptionFalsifierSnapshots,
);

export type StaffAdoptionFalsifierSnapshot = typeof staffAdoptionFalsifierSnapshots.$inferSelect;
export type InsertStaffAdoptionFalsifierSnapshot = z.infer<
  typeof insertStaffAdoptionFalsifierSnapshotSchema
>;
